import React, { useCallback, useMemo, useRef, useState } from "react";
import { Calendar, Callout, DayOfWeek, DirectionalHint, Icon, TooltipDelay, TooltipHost } from "office-ui-fabric-react";
import { TimeReportingPeriod, findPeriodForDate } from "../../../../../store/TimeReportingPeriodsStore";
import { FormatDate } from "../../../../utils/common";
import { DayPickerStrings } from "../../../../common/inputs/DatePickerInput";
import "./periodNavigator.css";

type PeriodNavigatorProps = {
    periods: TimeReportingPeriod[];
    selectedPeriod: TimeReportingPeriod | null;
    onPeriodChange: (period: TimeReportingPeriod) => void;
    firstDayOfWeek: DayOfWeek;
};

const PeriodNavigator = (props: Readonly<PeriodNavigatorProps>) => {
    const [today] = useState(() => new Date());
    const { periods, selectedPeriod, onPeriodChange, firstDayOfWeek } = props;

    const [showCalendar, setShowCalendar] = useState(false);
    const calendarLabelRef = useRef<HTMLSpanElement>(null);

    const currentIndex = selectedPeriod ? periods.findIndex(p => p.id === selectedPeriod.id) : -1;
    const hasPreviousPeriod = currentIndex > 0;
    const hasNextPeriod = currentIndex >= 0 && currentIndex < periods.length - 1;

    const minDate = periods[0]?.attributes.StartDate;
    const maxDate = periods[periods.length - 1]?.attributes.EndDate;

    const showTodayButton = useMemo(() => {
        if (selectedPeriod && selectedPeriod.attributes.StartDate <= today && selectedPeriod.attributes.EndDate >= today) {
            return false;
        }
        return !!findPeriodForDate(today, periods);
    }, [selectedPeriod, periods]);

    const handleNavigateToToday = useCallback(() => {
        const todayPeriod = findPeriodForDate(today, periods);
        if (todayPeriod) {
            onPeriodChange(todayPeriod);
        }
    }, [periods, onPeriodChange]);

    if (!periods.length) {
        return null;
    }

    return (
        <div className="period-navigation">
            <TooltipHost
                content={
                    hasPreviousPeriod
                        ? undefined
                        : "The previous time reporting period has not been defined. Please contact your administrator to create one and enable time tracking."
                }
                delay={TooltipDelay.zero}>
                <Icon
                    className={`chevron-icon ${hasPreviousPeriod ? "clickable" : "disabled"}`}
                    iconName="ChevronLeft"
                    onClick={hasPreviousPeriod ? () => onPeriodChange(periods[currentIndex - 1]) : undefined}
                />
            </TooltipHost>
            <span className="calendar clickable" onClick={() => setShowCalendar(true)} ref={calendarLabelRef}>
                {selectedPeriod
                    ? `${FormatDate(selectedPeriod.attributes.StartDate)} - ${FormatDate(selectedPeriod.attributes.EndDate)}`
                    : "N/A"}
            </span>
            <TooltipHost
                content={
                    hasNextPeriod
                        ? undefined
                        : "The next time reporting period has not been defined. Please contact your administrator to create one and enable time tracking."
                }
                delay={TooltipDelay.zero}>
                <Icon
                    className={`chevron-icon ${hasNextPeriod ? "clickable" : "disabled"}`}
                    iconName="ChevronRight"
                    onClick={hasNextPeriod ? () => onPeriodChange(periods[currentIndex + 1]) : undefined}
                />
            </TooltipHost>
            {showTodayButton && (
                <button className="period-today-button" onClick={handleNavigateToToday} title="Today">
                    <Icon iconName="ReplyMirrored" />
                </button>
            )}
            {showCalendar && calendarLabelRef.current && (
                <Callout
                    isBeakVisible={false}
                    className="ms-DatePicker-callout"
                    doNotLayer={false}
                    target={calendarLabelRef.current}
                    gapSpace={5}
                    preventDismissOnScroll={true}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    onDismiss={() => setShowCalendar(false)}>
                    <Calendar
                        onSelectDate={date => {
                            if (date) {
                                const period = findPeriodForDate(date, periods);
                                if (period) {
                                    onPeriodChange(period);
                                }
                            }
                            setShowCalendar(false);
                        }}
                        onDismiss={() => setShowCalendar(false)}
                        value={selectedPeriod?.attributes.StartDate || new Date()}
                        strings={DayPickerStrings}
                        showGoToToday
                        firstDayOfWeek={firstDayOfWeek}
                        minDate={minDate}
                        maxDate={maxDate}
                    />
                </Callout>
            )}
        </div>
    );
};

export default PeriodNavigator;
